/**
 * Reads the cached OncoEMR user roster for assignee and recipient pickers.
 * @module useOncoemrUsers
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { OncoemrUser } from "../data-model/oncoemrUsersModel";
import { RootState } from "../redux/store/store";
import {
  buildAssigneeSelectOptions,
  filterOncoemrUsersByQuery,
  mapOncoemrUsersToSelectOptions,
} from "../utils/oncoemrUsersHelpers";

/**
 * Exposes org-scoped OncoEMR users from Redux and memoized picker helpers.
 *
 * @returns Cached users, loading/error state, and option builders.
 */
export const useOncoemrUsers = () => {
  const { orgId, users, loading, error } = useSelector(
    (state: RootState) => state.oncoemrUsers,
  );

  const assigneeOptions = useMemo(
    () => buildAssigneeSelectOptions(users),
    [users],
  );

  const recipientOptions = useMemo(
    () => mapOncoemrUsersToSelectOptions(users),
    [users],
  );

  /**
   * Filters the cached roster by display name (client-side autocomplete).
   *
   * @param query - Substring to match against `first_and_last`.
   * @returns Matching users from the cached org list.
   */
  const filterUsers = (query: string): OncoemrUser[] =>
    filterOncoemrUsersByQuery(users, query);

  return {
    orgId,
    users,
    loading,
    error,
    assigneeOptions,
    recipientOptions,
    filterUsers,
  };
};
